'use client';

// The short chime both apps play when a message, broadcast, outbid or clerk
// event arrives, and the per-person, per-device switch that silences it (BL-2588).

import { useCallback } from 'react';

import { useStoredPreference } from './useStoredPreference';

/** The stored value that means "off". Anything else, including nothing stored, is on. */
const SOUND_OFF = 'off';

/** The two tones of the chime, in Hz, and how far apart they start, in seconds. */
const CHIME_TONES = [880, 1320] as const;
const TONE_GAP_S = 0.12;
const TONE_LENGTH_S = 0.3;
const PEAK_GAIN = 0.15;

/** One AudioContext per page, shared across bundles (ADR 008 — Global Singletons). */
const AUDIO_KEY = Symbol.for('auctogon.notificationSound.audio');

interface AudioSlot {
    context: AudioContext | null;
    unlockArmed: boolean;
}

type AudioContextCtor = new () => AudioContext;

function audioSlot(): AudioSlot {
    const g = globalThis as unknown as Record<symbol, AudioSlot | undefined>;
    let slot = g[AUDIO_KEY];
    if (!slot) {
        slot = { context: null, unlockArmed: false };
        g[AUDIO_KEY] = slot;
    }
    return slot;
}

function audioCtor(): AudioContextCtor | null {
    if (typeof window === 'undefined') return null;
    const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
    return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Browsers start an AudioContext suspended until the person interacts with the
 * page. The first click or key press resumes it, so later chimes are heard.
 */
function armUnlock(slot: AudioSlot): void {
    if (slot.unlockArmed || typeof window === 'undefined') return;
    slot.unlockArmed = true;
    const unlock = () => {
        void slot.context?.resume().catch(() => undefined);
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
}

function getContext(): AudioContext | null {
    const slot = audioSlot();
    if (slot.context) return slot.context;
    const Ctor = audioCtor();
    if (!Ctor) return null;
    slot.context = new Ctor();
    armUnlock(slot);
    return slot.context;
}

/**
 * Play the chime once. Best-effort: a browser without Web Audio, or one that
 * still blocks sound before the first interaction, plays nothing and never throws.
 *
 * @sideEffect Starts two short oscillator tones on the shared AudioContext.
 */
function playChime(): void {
    try {
        const ctx = getContext();
        if (!ctx) return;
        const start = ctx.currentTime;
        CHIME_TONES.forEach((frequency, i) => {
            const at = start + i * TONE_GAP_S;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, at);
            gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, at + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, at + TONE_LENGTH_S);
            osc.connect(gain).connect(ctx.destination);
            osc.start(at);
            osc.stop(at + TONE_LENGTH_S + 0.02);
        });
    } catch {
        // Sound is a courtesy beside the toast: a failed chime must never break it.
    }
}

/**
 * The localStorage key for one person's sound switch (rule #16: user-scoped).
 *
 * @param scopeId - the signed-in person's id (staff user or customer user).
 * @returns the storage key.
 */
export function soundPreferenceKey(scopeId: string): string {
    return `notification-sound:${scopeId}`;
}

/** What {@link useNotificationSound} returns. */
export interface NotificationSound {
    /** Whether the chime plays. On unless this person turned it off on this device. */
    enabled: boolean;
    /** Turn the chime on or off for this person on this device. */
    setEnabled: (next: boolean) => void;
    /** Play the chime, unless it is off or the person is not known yet. */
    play: () => void;
}

/**
 * The notification chime and its per-device switch for one person.
 *
 * @param scopeId - the signed-in person's id, or null before it is known
 *   (nothing plays and the switch reads on).
 * @returns {@link NotificationSound}.
 * @sideEffect `play` makes a sound; `setEnabled` writes localStorage.
 */
export function useNotificationSound(scopeId: string | null): NotificationSound {
    const { value, setValue } = useStoredPreference(scopeId ? soundPreferenceKey(scopeId) : null);
    const enabled = value !== SOUND_OFF;
    const setEnabled = useCallback((next: boolean) => setValue(next ? null : SOUND_OFF), [setValue]);
    const play = useCallback(() => {
        if (scopeId && enabled) playChime();
    }, [scopeId, enabled]);
    return { enabled, setEnabled, play };
}
